import { Group, Table, Loader } from '@mantine/core';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { fetchProductsList, IProductResponse, IProduct } from '../../../libraries/products';
import { AppState } from '../../client-redux/reducers';
import { ICurrency } from '../../../libraries/currencies';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import PaginationComponent from '../../components/pagination';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import Header from '../../components/util/header';
import { Language } from '../../languages/languageHandler';
import { formatCurrAmount } from '../../utils/script';
import AddEditProduct from './add-edit-product';
import './styles.scss';

const Products = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currency = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const controller = new AbortController();

  const [loading, setLoading] = useState(false);
  const [, setTotalRowsResponse] = useState<number | undefined>();
  const [offset, setOffset] = useState<number>(0);
  const { limit = 10, setLimit, tableRef } = useFitTableRows({ bufferRows: 1, minHeightForDynamic: 400 });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [products, setProducts] = useState<IProductResponse>();
  const [noResults, setNoResults] = useState<boolean>(true);
  const [statusOperCode, setStatusOperCode] = useState<number | undefined>();
  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>({ eid: 0 });

  const [modalOpened, setModalOpened] = useState(false);
  const [modalMode, setModalMode] = useState<'new' | 'edit'>('edit');
  const [activeProduct, setActiveProduct] = useState<IProduct | undefined>(undefined);

  useEffect(() => {
    if (limit) {
      loadProductList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadProductList = () => {
    if (loading) {
      controller.abort();
    }

    setLoading(true);
    setProducts(undefined);
    const shouldFetchTotalRows = offset === 0;

    fetchProductsList()
      .then(response => {
        if (response.statusOper.code === 0) {
          setProducts(response);
          if (shouldFetchTotalRows && typeof response.totalRows === 'number') {
            setTotalRowsResponse(response.totalRows);
          }
          setNoResults(false);
          setStatusOperCode(response.statusOper.code);
        } else {
          setNoResults(true);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      })
      .finally(() => setLoading(false));
  };

  const changePage = (page: number) => {
    setOffset((page - 1) * limit);
    setCurrentPage(page);
  };

  const resultsSelect = (newValue: number) => {
    setLimit(Number(newValue));
    setCurrentPage(1);
    setOffset(0);
  };

  const openEditModal = (product: IProduct) => {
    setActiveProduct(product);
    setModalMode('edit');
    setModalOpened(true);
  };

  const openNewModal = () => {
    setActiveProduct(undefined);
    setModalMode('new');
    setModalOpened(true);
  };

  return (
    <div className="products-list">
      <Header pageTitle={lang.PRODUCTS_TITLE} />
      <FilterBar onDataChange={handleFilterBarOnDataChange} onSearch={loadProductList} addButton={openNewModal} />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.PRODUCTS_ID}</Table.Th>
              <Table.Th>{lang.PRODUCTS_NAME}</Table.Th>
              <Table.Th>{lang.PRODUCTS_PRICE}</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : noResults ? (
              <Table.Tr>
                <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            ) : (
              products?.list?.map((product, index) => (
                <Table.Tr key={index} onClick={() => openEditModal(product)}>
                  <Table.Td>{product.productid}</Table.Td>
                  <Table.Td>{product.name}</Table.Td>
                  <Table.Td>{formatCurrAmount(product.price, currency[0])}</Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={products?.list?.length ? Math.ceil(products.list.length / limit) : 0}
          statusOperCode={statusOperCode}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>

      {modalOpened && (
        <AddEditProduct
          opened={modalOpened}
          close={() => setModalOpened(false)}
          mode={modalMode}
          product={activeProduct}
          onSaved={loadProductList}
        />
      )}
    </div>
  );
};

export default Products;
